import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import type {
  ClaimDetailDto,
  CurationRetractionReason,
  ManualClaimSourceType,
} from '@common/synapse/introspection-types';
import {
  UserObjectDimension,
  USER_OBJECT_DIMENSIONS,
  DIMENSION_LABELS,
} from '@common/synapse/introspection-types';
import {
  addManualClaim,
  correctClaim,
  restoreClaim,
  retractClaim,
} from '../../lib/api/synapseIntrospection';

const RETRACTION_REASONS: { value: CurationRetractionReason; label: string }[] = [
  { value: 'misattribution', label: 'Misattribution' },
  { value: 'low_quality', label: 'Low quality' },
  { value: 'privacy_concern', label: 'Privacy concern' },
  { value: 'study_protocol_exclusion', label: 'Study protocol exclusion' },
  { value: 'other', label: 'Other' },
];

const MANUAL_SOURCE_TYPES: { value: ManualClaimSourceType; label: string }[] = [
  { value: 'intake_questionnaire', label: 'Intake questionnaire' },
  { value: 'pre_study_interview', label: 'Pre-study interview' },
  { value: 'correction', label: 'Correction' },
];

export interface ClaimCurationPanelProps {
  userId: string;
  detail: ClaimDetailDto;
  accessToken?: string;
  onUpdated: () => void;
}

const ClaimCurationPanel = ({ userId, detail, accessToken, onUpdated }: ClaimCurationPanelProps) => {
  const claim = detail.claim;
  const isRetracted = claim.provenance === 'retracted';

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [retractOpen, setRetractOpen] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [retractReason, setRetractReason] = useState<CurationRetractionReason>('misattribution');
  const [retractNote, setRetractNote] = useState('');

  const [correctValue, setCorrectValue] = useState(claim.value);
  const [correctConfidence, setCorrectConfidence] = useState(String(claim.confidence));
  const [correctKey, setCorrectKey] = useState(claim.predicate);
  const [correctScope, setCorrectScope] = useState('global');
  const [correctNote, setCorrectNote] = useState('');

  const runAction = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onUpdated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Curation
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {!isRetracted ? (
          <>
            <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => setRetractOpen(true)}>
              Retract
            </Button>
            <Button size="small" variant="outlined" disabled={busy} onClick={() => setCorrectOpen(true)}>
              Correct
            </Button>
          </>
        ) : (
          <Button
            size="small"
            variant="outlined"
            color="success"
            disabled={busy}
            onClick={() => void runAction(() => restoreClaim(userId, claim.id, accessToken))}
          >
            Restore
          </Button>
        )}
      </Stack>

      <Dialog open={retractOpen} onClose={() => setRetractOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Retract claim</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Retracted claims are hidden from retrieval but remain visible here for audit.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Reason</InputLabel>
            <Select
              label="Reason"
              value={retractReason}
              onChange={(e) => setRetractReason(e.target.value as CurationRetractionReason)}
            >
              {RETRACTION_REASONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Note (optional)"
            value={retractNote}
            onChange={(e) => setRetractNote(e.target.value)}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetractOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={busy}
            onClick={() =>
              void runAction(async () => {
                await retractClaim(userId, claim.id, { reason: retractReason, note: retractNote || undefined }, accessToken);
                setRetractOpen(false);
              })
            }
          >
            Retract
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={correctOpen} onClose={() => setCorrectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Correct claim</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Evidence spans and source turns cannot be edited. Original values are preserved in the audit log.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Predicate / key" value={correctKey} onChange={(e) => setCorrectKey(e.target.value)} fullWidth />
            <TextField label="Value" value={correctValue} onChange={(e) => setCorrectValue(e.target.value)} fullWidth />
            <TextField
              label="Confidence (0–1)"
              value={correctConfidence}
              onChange={(e) => setCorrectConfidence(e.target.value)}
              fullWidth
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.01 }}
            />
            <TextField label="Scope" value={correctScope} onChange={(e) => setCorrectScope(e.target.value)} fullWidth />
            <TextField label="Note (optional)" value={correctNote} onChange={(e) => setCorrectNote(e.target.value)} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrectOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() =>
              void runAction(async () => {
                const confidence = parseFloat(correctConfidence);
                await correctClaim(
                  userId,
                  claim.id,
                  {
                    key: correctKey !== claim.predicate ? correctKey : undefined,
                    value: correctValue !== claim.value ? correctValue : undefined,
                    confidence: !Number.isNaN(confidence) && confidence !== claim.confidence ? confidence : undefined,
                    scope: correctScope ? [correctScope] : undefined,
                    note: correctNote || undefined,
                  },
                  accessToken
                );
                setCorrectOpen(false);
              })
            }
          >
            Save correction
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export interface AddManualClaimDialogProps {
  open: boolean;
  userId: string;
  dimension?: UserObjectDimension | null;
  accessToken?: string;
  onClose: () => void;
  onAdded: (claimId: string) => void;
}

export function AddManualClaimDialog({
  open,
  userId,
  dimension,
  accessToken,
  onClose,
  onAdded,
}: AddManualClaimDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [predicate, setPredicate] = useState('interest_topic');
  const [value, setValue] = useState('');
  const [claimDimension, setClaimDimension] = useState<UserObjectDimension>(dimension ?? UserObjectDimension.Interest);
  const [confidence, setConfidence] = useState('0.85');
  const [sourceType, setSourceType] = useState<ManualClaimSourceType>('pre_study_interview');
  const [rationale, setRationale] = useState('');

  const handleSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      const parsedConfidence = parseFloat(confidence);
      const result = await addManualClaim(
        userId,
        {
          predicate,
          value,
          dimension: claimDimension,
          confidence: parsedConfidence,
          sourceType,
          rationale,
          scope: ['global'],
        },
        accessToken
      );
      onAdded(result.claim.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add claim');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add manual claim</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manual claims require a rationale and appear with a MANUAL badge throughout the inspector.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Predicate" value={predicate} onChange={(e) => setPredicate(e.target.value)} fullWidth required />
          <TextField label="Value" value={value} onChange={(e) => setValue(e.target.value)} fullWidth required />
          <FormControl fullWidth>
            <InputLabel>Dimension</InputLabel>
            <Select
              label="Dimension"
              value={claimDimension}
              onChange={(e) => setClaimDimension(e.target.value as UserObjectDimension)}
            >
              {USER_OBJECT_DIMENSIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {DIMENSION_LABELS[d]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Confidence (0–1)"
            value={confidence}
            onChange={(e) => setConfidence(e.target.value)}
            fullWidth
            type="number"
            inputProps={{ min: 0, max: 1, step: 0.01 }}
          />
          <FormControl fullWidth>
            <InputLabel>Source type</InputLabel>
            <Select
              label="Source type"
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value as ManualClaimSourceType)}
            >
              {MANUAL_SOURCE_TYPES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            fullWidth
            required
            multiline
            minRows={3}
            helperText="Why is this claim being added outside of conversation extraction?"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={busy || !value.trim() || !rationale.trim()} onClick={() => void handleSubmit()}>
          Add claim
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ClaimCurationPanel;
